import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const empty = () => ({ version: "", title: "", date: new Date().toISOString().slice(0, 10), items: "", published: true, announce: true });

export function ReleasesTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/releases").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    const body = { ...editing, items: editing.items.split("\n").map(s => s.trim()).filter(Boolean) };
    if (!body.version || !body.title || body.items.length === 0) { toast.error("Version, title and at least one item are required"); return; }
    setBusy(true);
    try {
      if (editing.id) await api.put(`/admin/releases/${editing.id}`, body); else await api.post("/admin/releases", body);
      toast.success("Release saved"); setEditing(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save"); }
    finally { setBusy(false); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete release v${r.version}?`)) return;
    try { await api.delete(`/admin/releases/${r.id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="releases-tab">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Records</div>
          <h2 className="font-display font-black text-3xl mt-1">Release notes</h2>
          <p className="opacity-60 text-sm mt-1">Shown on the What's New page. The newest published release pops up once for each user after they next open the app.</p>
        </div>
        <button type="button" onClick={() => setEditing(empty())} data-testid="release-add-btn" className="btn-volt px-4 py-2.5 rounded inline-flex items-center gap-2 text-[10px] tracking-widest uppercase"><Plus className="w-3 h-3" /> New release</button>
      </div>
      <div className="space-y-2" data-testid="releases-admin-list">
        {rows.map(r => (
          <div key={r.id} className="card-tactical rounded-md p-4 flex flex-wrap items-start gap-3" data-testid={`release-admin-${r.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap"><span className="font-display font-black text-xl">v{r.version}</span><span className="text-xs opacity-60">{r.date}</span>{!r.published && <span className="text-[9px] tracking-widest uppercase px-2 py-0.5 rounded border border-amber-400/50 text-amber-300">Draft</span>}</div>
              <div className="font-semibold text-sm">{r.title}</div>
              <div className="text-xs opacity-60 mt-1">{r.items.length} item{r.items.length === 1 ? "" : "s"}</div>
            </div>
            <div className="flex gap-1">
              <button type="button" onClick={() => setEditing({ ...r, items: r.items.join("\n") })} data-testid={`release-edit-${r.id}`} className="p-2 rounded border border-border opacity-70 hover:opacity-100"><Pencil className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => remove(r)} data-testid={`release-delete-${r.id}`} className="p-2 rounded border border-red-400/50 text-red-300 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!editing} onOpenChange={v => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-xl" data-testid="release-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">{editing?.id ? "Edit release" : "New release"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[10px] tracking-widest uppercase opacity-70">Version</Label><Input value={editing.version} onChange={e => setEditing({ ...editing, version: e.target.value })} placeholder="2.7" data-testid="release-version" /></div>
                <div><Label className="text-[10px] tracking-widest uppercase opacity-70">Date</Label><Input type="date" value={editing.date} onChange={e => setEditing({ ...editing, date: e.target.value })} data-testid="release-date" /></div>
              </div>
              <div><Label className="text-[10px] tracking-widest uppercase opacity-70">Title</Label><Input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} placeholder="Short headline for this release" data-testid="release-title" /></div>
              <div><Label className="text-[10px] tracking-widest uppercase opacity-70">What changed — one item per line</Label><Textarea rows={8} value={editing.items} onChange={e => setEditing({ ...editing, items: e.target.value })} data-testid="release-items" /></div>
              <label className="inline-flex items-center gap-2 text-[10px] tracking-widest uppercase opacity-80"><Switch checked={!!editing.published} onCheckedChange={v => setEditing({ ...editing, published: v })} data-testid="release-published" /> Published (visible to everyone)</label>
              {!editing.announced_at ? (
                <label className="flex items-center gap-2 text-[10px] tracking-widest uppercase opacity-80"><Switch checked={editing.announce !== false} onCheckedChange={v => setEditing({ ...editing, announce: v })} data-testid="release-announce" /> Notify admins &amp; coaches (push + email) when published</label>
              ) : (
                <div className="text-[10px] tracking-widest uppercase opacity-60" data-testid="release-announced">Announced {editing.announced_at.slice(0, 10)}</div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 rounded border border-border text-[10px] tracking-widest uppercase">Cancel</button>
                <button type="button" onClick={save} disabled={busy} data-testid="release-save" className="btn-volt px-5 py-2 rounded text-[10px] tracking-widest uppercase disabled:opacity-40">{busy ? "Saving…" : "Save"}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
